import { Box, Grid, Skeleton } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import CountLimit from '../usage/countLimit';
import SoftLimit from '../usage/softLimit';
import ThroughputLimit from '../usage/throughputLimit';
import Access from '../usage/access';
import Enforcement from '../usage/enforcement';
import { HttpContext } from '../../../context/HttpContext';
import { MeContext } from '../../../context/MeContext';
import SoftLimitExceededBanner from './SoftLimitExceededBanner';

const UsageAndLimits = () => {
  const { authAxios } = useContext(HttpContext);
  const { state } = useContext(MeContext);
  const watermarkFree = state?.watermarkFree;
  const [entitlements, setEntitlements] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isLimitReached = (entitlement) => {
    if (
      !entitlement ||
      typeof entitlement.currentValue !== 'number' ||
      typeof entitlement.limitValue !== 'number'
    ) {
      return false;
    }
    return entitlement.currentValue >= entitlement.limitValue;
  };

  const fetchEntitlements = async () => {
    try {
      setIsLoading(true);
      const data = await await authAxios.get(
        '/internal/v1/billing/subscriptions/current'
      );

      const {
        subscription,
        organisation,
        trial,
        entitlements: orgEntitlements,
      } = data.data;
      setEntitlements(
        subscription?.entitlements ||
          organisation?.entitlements ||
          trial?.entitlements ||
          orgEntitlements
      );
    } catch (error) {
      console.error('Error fetching entitlements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntitlements();
  }, []);

  const renderSkeletonCards = () => {
    return Array.from({ length: 6 }, (_, index) => (
      <Box
        key={`skeleton-${index}`}
        p={4}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="15px"
        w="100%"
        h="8rem"
      >
        <Skeleton height="20px" mb={2} />
        <Skeleton height="16px" mb={2} />
        <Skeleton height="16px" width="60%" />
      </Box>
    ));
  };

  const TEMPLATE_DERIVED_KEYS = [
    'pass_download_pages',
    'custom_download_forms',
  ];

  const DISPLAY_ORDER = [
    'passes',
    'pass_templates',
    'entities',
    'seats',
    'passentry_reader_logins',
    'pass_download_pages',
    'custom_download_forms',
    'passentry_watermark',
    'sso',
  ];

  const renderEntitlements = () => {
    if (!entitlements) return null;

    const banners = [];
    const entitlementCards = [];
    const passTemplatesEntitlement = entitlements.find(
      (e) => e.key === 'pass_templates'
    );

    const visibleEntitlements = watermarkFree
      ? entitlements.filter((e) => e.key !== 'passentry_watermark')
      : entitlements;

    const sortedEntitlements = [...visibleEntitlements].sort((a, b) => {
      const indexA = DISPLAY_ORDER.indexOf(a.key);
      const indexB = DISPLAY_ORDER.indexOf(b.key);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    sortedEntitlements.forEach((item) => {
      if (item.kind === 'softLimit' && isLimitReached(item)) {
        banners.push(
          <SoftLimitExceededBanner
            key={`banner-${item.kind}:${item.key}`}
            entitlement={item}
          />
        );
      }

      const displayItem =
        TEMPLATE_DERIVED_KEYS.includes(item.key) && passTemplatesEntitlement
          ? { ...item, limitValue: passTemplatesEntitlement.limitValue }
          : item;

      entitlementCards.push(
        <Box
          key={`${item.kind}:${item.key}`}
          p={6}
          bg="white"
          border="1px solid"
          borderColor={
            item.kind == 'softLimit' && isLimitReached(displayItem)
              ? 'dangerRed'
              : 'gray.200'
          }
          borderRadius="10px"
          w="100%"
        >
          {item.kind == 'countLimit' && (
            <CountLimit entitlement={displayItem} />
          )}
          {item.kind == 'softLimit' && (
            <SoftLimit
              entitlement={displayItem}
              isLimitReached={isLimitReached}
            />
          )}
          {item.kind == 'throughputLimit' && (
            <ThroughputLimit entitlement={displayItem} />
          )}
          {item.kind == 'access' && <Access entitlement={displayItem} />}
          {item.kind == 'enforcement' && (
            <Enforcement entitlement={displayItem} />
          )}
        </Box>
      );
    });

    return { banners, entitlementCards };
  };

  return (
    <>
      <Box w="100%">
        {!isLoading && entitlements && renderEntitlements().banners}

        <Grid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
          }}
          gap={6}
        >
          {isLoading
            ? renderSkeletonCards()
            : entitlements && renderEntitlements().entitlementCards}
        </Grid>
      </Box>
    </>
  );
};

export default UsageAndLimits;
